import { router } from '@inertiajs/vue3'

import type {
  ConfirmationHandlers,
  DuplicateCheckResult
} from '@/domains/command-center/handlers/product/productCommands.types'
import type {
  ActionCategory,
  ActionCommand,
  SearchResultCommand,
  SkuMatchInfo,
  WorkflowTransition
} from '@/domains/command-center/types'
import type { IndexProduct } from '@/domains/products/types/models/IndexProduct.types'

import {
  associateVariantsToProductExecute,
  enhanceGenerateFullExecute
} from '@/domains/command-center/handlers/product/enhanceExecute'
import { SEARCH_SCOPES } from '@/domains/command-center/services/searchScopes'
import { formatBarcodesForDisplay } from '@/domains/command-center/utils/barcodeUtils'

const getProductCategory = (): ActionCategory => ({
  id: 'cat-products',
  label: 'Products',
  // Every product route is behind `can:catalog.view`, so without it these all 403 on arrival.
  permission: { action: 'view', subject: 'catalog' },
  actions: [
    {
      id: 'action-products-new',
      type: 'action',
      label: 'Create a New Product',
      icon: 'add',
      keywords: ['create', 'add', 'new', 'product'],
      execute: () => router.get(route('admin.products.new')),
      onSuccess: 'closeMenu'
    },
    {
      id: 'action-generate-family-static',
      type: 'action',
      label: 'Generate a New Product (from Barcodes)',
      icon: 'stars_2',
      keywords: ['generate', 'family', 'barcode', 'product set', 'group', 'ean', 'upc', 'create'],
      execute: () => {
        // Placeholder, effectively bound in commandGenerator
      },
      onSelect: 'showToast',
      onSuccess: 'closeMenu'
    },
    {
      id: 'action-initiate-add-variants',
      type: 'action',
      label: `Add Variants to Existing Product`,
      icon: 'stars_2',
      keywords: ['add', 'variants', 'link'],
      execute: () => {
        // Placeholder, effectively bound in commandGenerator
      }
    }
  ]
})

/**
 * Find SKU matches from search results for the given barcodes
 * Uses Set for O(1) lookup performance instead of Array.includes() O(n)
 * @param barcodes - The barcodes being used for product generation
 * @param searchResults - Current search results that may contain SKU matches
 * @returns Array of SKU match info for barcodes that already exist
 */
const findExistingSkuMatches = (barcodes: string[], searchResults: SearchResultCommand[]): SkuMatchInfo[] => {
  const normalizedBarcodes = new Set(barcodes.map(b => b.toLowerCase()))

  if (normalizedBarcodes.size === 0) {
    return []
  }

  return searchResults
    .filter(
      result =>
        result.skuMatch?.hasMatch &&
        result.skuMatch.matchedSku &&
        normalizedBarcodes.has(result.skuMatch.matchedSku.toLowerCase())
    )
    .map(result => result.skuMatch!)
}

/** One line of the duplicate warning — a single variant that already holds the barcode. */
interface VariantLine {
  matchedSku: string
  productTitle: string
  variantOptions: string
}

const ONE_SKU_CLOSING =
  'Creating another product with this barcode may cause inventory tracking and point-of-sale issues.'

const MANY_SKU_CLOSING =
  'Creating products with duplicate barcodes may cause inventory tracking and point-of-sale issues.'

/**
 * One line per matched variant, not per matched product. A product that holds the barcode on two
 * variants gets two lines, so the warning never hides one of them.
 */
const variantLines = (matches: SkuMatchInfo[]): VariantLine[] =>
  matches.flatMap(match => {
    // A match with no variant list still earns one line — the product itself is the conflict.
    const variants = match.matchedVariants?.length ? match.matchedVariants : [undefined]

    return variants.map(variant => ({
      matchedSku: match.matchedSku ?? '',
      productTitle: match.productTitle ?? '',
      variantOptions: variant?.variantOptions ?? ''
    }))
  })

const productLabel = (line: VariantLine): string =>
  line.variantOptions ? `${line.productTitle} (${line.variantOptions})` : line.productTitle

/**
 * Build a user-friendly confirmation message for duplicate SKU warning
 * @param matches - The SKU matches found
 * @returns Formatted message string
 */
const buildDuplicateSkuMessage = (matches: SkuMatchInfo[]): string => {
  const lines = variantLines(matches)

  if (lines.length === 0) {
    return ''
  }

  const distinctSkuCount = new Set(lines.map(line => line.matchedSku.toLowerCase())).size

  // One barcode, however many variants hold it: name the barcode once, then list the variants.
  if (distinctSkuCount === 1) {
    const body =
      lines.length === 1 ? `"${productLabel(lines[0])}"` : lines.map(line => `• ${productLabel(line)}`).join('\n')

    return `The barcode "${lines[0].matchedSku}" is already registered to:\n\n${body}\n\n${ONE_SKU_CLOSING}`
  }

  const body = lines.map(line => `• ${line.matchedSku} → ${productLabel(line)}`).join('\n')

  return `The following barcodes are already in use:\n\n${body}\n\n${MANY_SKU_CLOSING}`
}

/** Whether these results can answer "does this barcode already exist". Only products can. */
const carriesProducts = (searchResults: SearchResultCommand[]): boolean =>
  searchResults.some(result => result.resultType === 'product')

/**
 * The product rows a barcode is checked against.
 *
 * ⚠ **The guard must not read its answer from the scope pills.** `searchResults` answers "what did
 * the user ask to see"; this guard asks "does this barcode already exist". They were the same
 * question while the palette searched only products, and stopped being the same the moment a pill
 * could switch products off — the results then carry orders and bills, no product matches, and the
 * guard reported every assigned barcode as free. A second product was created, silently.
 *
 * So: reuse what the palette already fetched when it actually holds products, and otherwise ask
 * the product scope directly. A lookup that fails leaves the caller with what it had rather than
 * blocking generation on a network error.
 *
 * Permission is not a concern here. Without `catalog.view` the whole product category is locked,
 * so nothing that reaches this guard is forbidden from querying products.
 *
 * The one wasted request is products-searched-and-nothing-matched, which looks up a second time
 * and again finds nothing. It costs one request on a deliberate click, never on a keystroke.
 */
const productsToCheck = async (
  barcodes: string[],
  searchResults: SearchResultCommand[]
): Promise<SearchResultCommand[]> => {
  if (carriesProducts(searchResults)) {
    return searchResults
  }

  const productScope = SEARCH_SCOPES.find(scope => scope.id === 'product')

  if (!productScope) {
    return searchResults
  }

  try {
    return await productScope.search(barcodes.join(','))
  } catch {
    return searchResults
  }
}

/**
 * Helper to check for duplicates and show confirmation if needed
 * Returns result object with proceed status and context
 * Note: Caller is responsible for clearQuery() - this function focuses on duplicate detection only
 */
const checkDuplicatesAndConfirm = async (
  barcodes: string[],
  searchResults: SearchResultCommand[],
  confirmDuplicateSku: ConfirmationHandlers['confirmDuplicateSku']
): Promise<DuplicateCheckResult> => {
  const existingMatches = findExistingSkuMatches(barcodes, await productsToCheck(barcodes, searchResults))

  if (existingMatches.length > 0) {
    return new Promise(resolve => {
      confirmDuplicateSku({
        message: buildDuplicateSkuMessage(existingMatches),
        accept: () => {
          resolve({ shouldProceed: true, wasCancelled: false, duplicatesFound: existingMatches.length })
        },
        reject: () => {
          resolve({ shouldProceed: false, wasCancelled: true, duplicatesFound: existingMatches.length })
        }
      })
    })
  }

  // No duplicates - proceed
  return Promise.resolve({ shouldProceed: true, wasCancelled: false, duplicatesFound: 0 })
}

const createProductFamilyAction = (
  barcodes: string[],
  clearQuery: () => void,
  searchResults: SearchResultCommand[] = [],
  isSearchLoading = false,
  waitForSearchComplete?: () => Promise<SearchResultCommand[]>,
  confirmationHandlers?: ConfirmationHandlers
): ActionCommand => {
  const hasValidBarcodes = barcodes.length > 0

  return {
    id: 'action-generate-family-static',
    type: 'action',
    label: hasValidBarcodes
      ? `Generate a New Product for ${formatBarcodesForDisplay(barcodes)}`
      : 'Generate a New Product (from Barcodes) ',
    icon: 'stars_2',
    disabled: !hasValidBarcodes,
    keywords: ['generate', 'family', 'barcode', 'product set', 'group', 'ean', 'upc', 'create'],
    execute: async (): Promise<void> => {
      if (!hasValidBarcodes) {
        return
      }

      // If no confirmation handlers provided, proceed without duplicate check
      if (!confirmationHandlers) {
        clearQuery()
        await enhanceGenerateFullExecute({ barcodes })
        return
      }

      const { confirmDuplicateSku, toast } = confirmationHandlers
      const barcodeDisplay = formatBarcodesForDisplay(barcodes)
      let toastId: string | undefined

      // If search is still loading, show a toast and wait for it to complete
      let resultsToCheck = searchResults
      if (isSearchLoading && waitForSearchComplete) {
        // Show loading toast while checking
        toastId = toast.showSkuCheckingToast(barcodeDisplay)

        // Wait for search with timeout handling
        const searchStartTime = Date.now()
        resultsToCheck = await waitForSearchComplete()
        const searchDuration = Date.now() - searchStartTime

        // Remove loading toast
        toast.removeToast(toastId)

        // If search took close to timeout (5s), warn user that check may be incomplete
        if (searchDuration >= 4500) {
          toast.showWarningToast(
            'Verification Timeout',
            'SKU check took too long. Proceeding with available results - duplicate detection may be incomplete.'
          )
        }
      }

      // Check for duplicates with the results
      const result = await checkDuplicatesAndConfirm(barcodes, resultsToCheck, confirmDuplicateSku)

      if (result.shouldProceed) {
        clearQuery()
        await enhanceGenerateFullExecute({ barcodes })
        return
      }

      // User cancelled - provide feedback
      if (result.wasCancelled) {
        toast.showInfoToast('Cancelled', 'Product generation was cancelled. The existing product was preserved.')
      }
    },
    onSelect: 'showToast',
    onSuccess: 'closeMenu'
  }
}

const createAddVariantsAction = (barcodes: string[]): ActionCommand => {
  const hasValidBarcodes = barcodes.length > 0

  return {
    id: 'action-initiate-add-variants',
    type: 'action',
    label: `Add Variants ${formatBarcodesForDisplay(barcodes)} to Existing Product`,
    icon: 'link',
    disabled: !hasValidBarcodes,
    keywords: ['add', 'variants', 'link', ...barcodes],
    execute: (): WorkflowTransition => ({
      type: 'workflowTransition',
      to: 'awaitingParentProduct',
      data: { pendingBarcodesForVariants: barcodes }
    })
  }
}

const handleSelectParentProduct = async (
  parentProduct: IndexProduct,
  pendingBarcodes: string[],
  resetWorkflow: () => void
) => {
  resetWorkflow()
  return associateVariantsToProductExecute({ product: parentProduct, barcodes: pendingBarcodes })
}

export {
  getProductCategory,
  createProductFamilyAction,
  createAddVariantsAction,
  handleSelectParentProduct,
  // Exported for testing
  findExistingSkuMatches,
  buildDuplicateSkuMessage
}
